/**
 * Getting a whole component into a screenshot.
 *
 * html2canvas photographs an element as it is laid out on screen, and a screen
 * hides things: a table inside `overflow-x-auto` shows only as many columns as
 * fit, and a panel inside `max-h-[95vh] overflow-auto` shows only as many rows as
 * fit. Exporting that gives the client a picture with columns and rows missing.
 *
 * `prepareForCapture` finds the elements that are ACTUALLY hiding content right
 * now (not every element that happens to have `overflow-hidden` for rounded
 * corners), and the returned `onclone` opens them up in the COPY html2canvas
 * renders. Their ancestors only lose their height cap, so they grow with the
 * content and keep their rounded corners. The page the user is looking at is never touched.
 *
 * WHAT IT LEAVES ALONE ON PURPOSE
 *   - text that is truncated with an ellipsis or a line clamp: opening it would
 *     spill the full text over its neighbours;
 *   - elements that hide nothing, so rounded-corner clipping on cards survives.
 *
 * WIDTH. Opening a scroll container does not make the component any wider, so the
 * root of the copy is widened by exactly the amount the widest container was
 * hiding. Height needs no such step: html2canvas measures the copy after
 * `onclone` has run, so a copy that grew taller is photographed whole.
 */

const CLIP_ATTR = 'data-export-clip';
const ROOT_ATTR = 'data-export-root';

const hides = (overflow: string) => overflow === 'auto' || overflow === 'scroll' || overflow === 'hidden';

export interface CaptureSetup {
  /** Pass to html2canvas as `onclone`. */
  onclone: (clonedDoc: Document) => void;
  /** Remove the temporary marks from the live page. Always call it, even if the capture failed. */
  restore: () => void;
  /** How many elements were hiding content. 0 means the component is already fully visible. */
  clippedCount: number;
}

export function prepareForCapture(root: HTMLElement): CaptureSetup {
  const marked: HTMLElement[] = [];
  const mark = (el: HTMLElement, kind: 'self' | 'ancestor') => {
    if (el.getAttribute(CLIP_ATTR) === 'self') return;
    if (!el.hasAttribute(CLIP_ATTR)) marked.push(el);
    el.setAttribute(CLIP_ATTR, kind);
  };

  root.setAttribute(ROOT_ATTR, 'true');

  let clippedCount = 0;
  let extraWidth = 0;
  const all: HTMLElement[] = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  for (const el of all) {
    const cs = window.getComputedStyle(el);
    const clamp = (cs as unknown as { webkitLineClamp?: string }).webkitLineClamp;
    if (cs.textOverflow === 'ellipsis' || (clamp && clamp !== 'none')) continue;

    const hidesX = hides(cs.overflowX) && el.scrollWidth > el.clientWidth + 1;
    const hidesY = hides(cs.overflowY) && el.scrollHeight > el.clientHeight + 1;
    if (!hidesX && !hidesY) continue;

    clippedCount += 1;
    mark(el, 'self');
    if (hidesX) extraWidth = Math.max(extraWidth, el.scrollWidth - el.clientWidth);
    // A parent with a height cap would clip the opened element again.
    for (let p = el.parentElement; p; p = p.parentElement) {
      mark(p, 'ancestor');
      if (p === root) break;
    }
  }

  const widenTo = extraWidth > 0 ? root.offsetWidth + extraWidth : 0;

  return {
    clippedCount,
    onclone: (clonedDoc: Document) => {
      clonedDoc.querySelectorAll<HTMLElement>(`[${CLIP_ATTR}]`).forEach((el) => {
        el.style.setProperty('max-height', 'none', 'important');
        el.style.setProperty('height', 'auto', 'important');
        if (el.getAttribute(CLIP_ATTR) === 'self') {
          // The element that was hiding content: open it, and let a flex child that was told to
          // shrink and scroll take the size of its content.
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('flex', 'none', 'important');
        }
        // An ancestor only loses its height cap. Its `overflow` stays, so a card's rounded-corner
        // clip survives; the box simply grows with its content and hides nothing.
      });
      if (widenTo > 0) {
        const cloneRoot = clonedDoc.querySelector<HTMLElement>(`[${ROOT_ATTR}]`);
        if (cloneRoot) {
          cloneRoot.style.setProperty('max-width', 'none', 'important');
          cloneRoot.style.setProperty('width', `${widenTo}px`, 'important');
        }
      }
    },
    restore: () => {
      marked.forEach((el) => el.removeAttribute(CLIP_ATTR));
      root.removeAttribute(ROOT_ATTR);
    },
  };
}
